import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Divider,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { salesReturnService } from '../../../services/salesReturnService'
import {
  SalesReturnCreate,
  SalesReturnUpdate,
  SalesReturnReason,
  SALES_RETURN_REASON_LABELS,
  SalesReturnItem
} from '../../../types/salesReturn'

interface InvoiceOption {
  id: string
  invoice_number: string
  customer_name: string
  invoice_date: string
  total_amount: number
}

interface InvoiceItem {
  id: string
  variant_id: string
  variant_name: string
  sku: string
  quantity: number
  unit_price: number
  line_total: number
  tax_rate: number
  hsn_code?: string
}

interface ReturnItem extends SalesReturnItem {
  selected: boolean
  return_quantity: number
  max_quantity: number
}

const SalesReturnForm: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [invoiceOptions, setInvoiceOptions] = useState<InvoiceOption[]>([])
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceOption | null>(null)
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([])
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([])
  
  const [formData, setFormData] = useState({
    invoice_id: '',
    return_date: new Date(),
    return_reason: SalesReturnReason.CUSTOMER_REQUEST,
    return_description: '',
  })

  const [totals, setTotals] = useState({
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    fetchInvoiceOptions()
    if (isEdit && id) {
      fetchSalesReturn(id)
    }
  }, [isEdit, id])

  useEffect(() => {
    if (selectedInvoice) {
      fetchInvoiceItems(selectedInvoice.id)
    }
  }, [selectedInvoice])

  useEffect(() => {
    calculateTotals()
  }, [returnItems])

  const fetchInvoiceOptions = async () => {
    try {
      const invoices = await salesReturnService.getReturnableInvoices()
      setInvoiceOptions(invoices)
    } catch (error) {
      console.error('Error fetching invoices:', error)
    }
  }

  const fetchInvoiceItems = async (invoiceId: string) => {
    try {
      const items = await salesReturnService.getReturnableInvoiceItems(invoiceId)
      setInvoiceItems(items)
      const mappedReturnItems: ReturnItem[] = items.map((item) => ({
        variant_id: item.variant_id,
        quantity: 0,
        unit_price: item.unit_price,
        tax_rate: item.tax_rate,
        hsn_code: item.hsn_code,
        line_total: 0,
        taxable_amount: 0,
        tax_amount: 0,
        variant: { id: item.variant_id, name: item.variant_name, sku: item.sku, price: item.unit_price, hsn_code: item.hsn_code },
        selected: false,
        return_quantity: 0,
        max_quantity: item.max_quantity,
      }))
      setReturnItems(mappedReturnItems)
    } catch (error) {
      console.error('Error fetching invoice items:', error)
    }
  }

  const fetchSalesReturn = async (returnId: string) => {
    try {
      const salesReturn = await salesReturnService.getById(returnId)
      setFormData({
        invoice_id: salesReturn.invoice_id,
        return_date: new Date(salesReturn.return_date),
        return_reason: salesReturn.return_reason as any,
        return_description: salesReturn.return_description || '',
      })
      
      // Find and set the selected invoice
      const invoice = invoiceOptions.find(inv => inv.id === salesReturn.invoice_id)
      if (invoice) {
        setSelectedInvoice(invoice)
      }
    } catch (error) {
      console.error('Error fetching sales return:', error)
    }
  }

  const calculateTotals = () => {
    const selectedItems = returnItems.filter(item => item.selected && item.return_quantity > 0)
    
    let subtotal = 0
    let tax_amount = 0

    selectedItems.forEach(item => {
      const lineSubtotal = item.return_quantity * item.unit_price
      const lineTax = (lineSubtotal * (item.tax_rate || 0)) / 100
      
      subtotal += lineSubtotal
      tax_amount += lineTax
    })

    const total_amount = subtotal + tax_amount

    setTotals({
      subtotal,
      tax_amount,
      total_amount,
    })
  }

  const handleInvoiceChange = (invoice: InvoiceOption | null) => {
    setSelectedInvoice(invoice)
    setFormData(prev => ({
      ...prev,
      invoice_id: invoice?.id || ''
    }))
    setReturnItems([])
  }

  const handleItemSelectionChange = (index: number, selected: boolean) => {
    const updatedItems = [...returnItems]
    updatedItems[index].selected = selected
    if (!selected) {
      updatedItems[index].return_quantity = 0
    }
    setReturnItems(updatedItems)
  }

  const handleQuantityChange = (index: number, quantity: number) => {
    const updatedItems = [...returnItems]
    const maxQuantity = updatedItems[index].max_quantity
    
    if (quantity >= 0 && quantity <= maxQuantity) {
      updatedItems[index].return_quantity = quantity
      updatedItems[index].quantity = quantity
      
      // Calculate line amounts
      const lineSubtotal = quantity * updatedItems[index].unit_price
      const lineTax = (lineSubtotal * (updatedItems[index].tax_rate || 0)) / 100
      
      updatedItems[index].taxable_amount = lineSubtotal
      updatedItems[index].tax_amount = lineTax
      updatedItems[index].line_total = lineSubtotal + lineTax
      
      setReturnItems(updatedItems)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.invoice_id) {
      newErrors.invoice_id = 'Please select an invoice'
    }

    if (!formData.return_description.trim()) {
      newErrors.return_description = 'Return description is required'
    }

    const selectedItems = returnItems.filter(item => item.selected && item.return_quantity > 0)
    if (selectedItems.length === 0) {
      newErrors.items = 'Please select at least one item to return'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const selectedItems = returnItems.filter(item => item.selected && item.return_quantity > 0)
      
      const returnData: SalesReturnCreate | SalesReturnUpdate = {
        invoice_id: formData.invoice_id,
        return_date: formData.return_date.toISOString().split('T')[0],
        return_reason: formData.return_reason,
        return_description: formData.return_description,
        items: selectedItems.map(item => ({
          variant_id: item.variant_id,
          quantity: item.return_quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          tax_rate: item.tax_rate || 0,
          hsn_code: item.hsn_code,
        }))
      }

      if (isEdit && id) {
        await salesReturnService.update(id, returnData as SalesReturnUpdate)
      } else {
        await salesReturnService.create(returnData as SalesReturnCreate)
      }

      navigate('/admin/documents/sales-returns')
    } catch (error) {
      console.error('Error saving sales return:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/admin/documents/sales-returns')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={600}>
            {isEdit ? 'Edit Sales Return' : 'Create Sales Return'}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Return Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={invoiceOptions}
                    getOptionLabel={(option) => `${option.invoice_number} - ${option.customer_name}`}
                    value={selectedInvoice}
                    onChange={(_, value) => handleInvoiceChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Invoice"
                        error={!!errors.invoice_id}
                        helperText={errors.invoice_id}
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.invoice_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.customer_name} • {new Date(option.invoice_date).toLocaleDateString('en-GB')} • ₹{option.total_amount.toLocaleString('en-IN')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Return Date"
                    value={formData.return_date}
                    onChange={(date) => setFormData(prev => ({ ...prev, return_date: date || new Date() }))}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.return_date,
                        helperText: errors.return_date,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.return_reason}>
                    <InputLabel>Return Reason</InputLabel>
                    <Select
                      value={formData.return_reason}
                      label="Return Reason"
                      onChange={(e) => setFormData(prev => ({ ...prev, return_reason: e.target.value as SalesReturnReason }))}
                    >
                      {Object.values(SalesReturnReason).map((reason) => (
                        <MenuItem key={reason} value={reason}>
                          {SALES_RETURN_REASON_LABELS[reason]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Return Description"
                    multiline
                    rows={3}
                    value={formData.return_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, return_description: e.target.value }))}
                    error={!!errors.return_description}
                    helperText={errors.return_description}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Items Section */}
            {selectedInvoice && invoiceItems.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 2, mt: 3 }}>
                <Typography variant="h6" fontWeight={600} mb={3}>
                  Select Items to Return
                </Typography>

                {errors.items && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.items}
                  </Alert>
                )}

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Available Qty</TableCell>
                        <TableCell align="right">Return Qty</TableCell>
                        <TableCell align="right">Line Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnItems.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={item.selected}
                              onChange={(e) => handleItemSelectionChange(index, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.variant?.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.variant?.sku}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            ₹{item.unit_price.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell align="right">
                            {item.max_quantity}
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={item.return_quantity}
                              onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                              disabled={!item.selected}
                              inputProps={{
                                min: 0,
                                max: item.max_quantity,
                                style: { textAlign: 'right' }
                              }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              ₹{(item.line_total || 0).toLocaleString('en-IN')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Grid>

          {/* Summary Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, borderRadius: 2, position: 'sticky', top: 24 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Return Summary
              </Typography>

              {selectedInvoice && (
                <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Selected Invoice
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedInvoice.invoice_number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedInvoice.customer_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(selectedInvoice.invoice_date).toLocaleDateString('en-GB')}
                    </Typography>
                    <Chip
                      label={`₹${selectedInvoice.total_amount.toLocaleString('en-IN')}`}
                      color="primary"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              )}

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{totals.subtotal.toLocaleString('en-IN')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Tax Amount:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{totals.tax_amount.toLocaleString('en-IN')}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight={600}>Total Return:</Typography>
                  <Typography variant="h6" fontWeight={600} color="error.main">
                    ₹{totals.total_amount.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 2 }}
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Return' : 'Create Return')}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  )
}

export default SalesReturnForm